/**
 * Executive KPI catalog — code-level source of truth (D4).
 *
 * Each entry defines a KPI the hero band can render. The list is
 * versioned with the code; admins pick from it in the dashboard editor
 * but cannot author new KPIs without a PR.
 */

import type { Permission } from '../auth/permissions';

export type ThresholdState = 'normal' | 'warn' | 'critical';

export interface KpiThresholds {
  /** Value at which the tile shows a warning glow. */
  warnAt: number;
  /** Value at which the tile shows a critical pulse. */
  criticalAt: number;
}

export interface KpiCatalogEntry {
  id: string;
  label: string;
  unit: 'count' | 'percent' | 'hours' | 'currency' | 'score';
  /** When set, hero-band tile glows/pulses on threshold breach. */
  thresholds?: KpiThresholds;
  /** Key for the 14-day sparkline timeseries. Null = no sparkline. */
  sparklineKey: string | null;
  /** Frontend route for click-through drill-down. */
  drillTo: string;
  /** KPI is hidden if the user lacks this permission. */
  permission: Permission;
}

export const KPI_CATALOG: readonly KpiCatalogEntry[] = [
  {
    id: 'open_work_orders',
    label: 'Open Work Orders',
    unit: 'count',
    thresholds: { warnAt: 120, criticalAt: 200 },
    sparklineKey: 'daily_open_wo_14d',
    drillTo: '/work-orders?status=open',
    permission: 'workorder.read',
  },
  {
    id: 'sla_at_risk',
    label: 'SLA At-Risk',
    unit: 'count',
    thresholds: { warnAt: 10, criticalAt: 25 },
    sparklineKey: 'daily_sla_risk_14d',
    drillTo: '/work-orders?status=open&filter=at-risk',
    permission: 'workorder.read',
  },
  {
    id: 'overdue_approvals',
    label: 'Overdue Approvals',
    unit: 'count',
    thresholds: { warnAt: 5, criticalAt: 15 },
    sparklineKey: 'daily_overdue_approvals_14d',
    drillTo: '/approvals?filter=overdue',
    permission: 'workorder.approve',
  },
  {
    id: 'crew_on_shift',
    label: 'Crew On Shift',
    unit: 'count',
    sparklineKey: null,
    drillTo: '/team',
    permission: 'workorder.read',
  },
  {
    id: 'vendor_spend_mtd',
    label: 'Vendor Spend MTD',
    unit: 'currency',
    thresholds: { warnAt: 250_000, criticalAt: 400_000 },
    sparklineKey: 'daily_vendor_spend_14d',
    drillTo: '/vendors?tab=spend',
    permission: 'insights.read',
  },
  {
    id: 'safety_incidents',
    label: 'Safety Incidents',
    unit: 'count',
    thresholds: { warnAt: 1, criticalAt: 3 },
    sparklineKey: 'daily_safety_14d',
    drillTo: '/safety',
    permission: 'insights.read',
  },
  {
    id: 'budget_variance',
    label: 'Budget Variance',
    unit: 'percent',
    thresholds: { warnAt: 10, criticalAt: 20 },
    sparklineKey: 'daily_budget_var_14d',
    drillTo: '/insights?tab=cost-forecast',
    permission: 'insights.read',
  },
  {
    id: 'backlog_age_avg',
    label: 'Avg Backlog Age',
    unit: 'hours',
    thresholds: { warnAt: 72, criticalAt: 168 },
    sparklineKey: 'daily_backlog_age_14d',
    drillTo: '/work-orders?sort=createdAt&dir=asc&status=open',
    permission: 'workorder.read',
  },
] as const;

export const KPI_CATALOG_BY_ID = Object.fromEntries(
  KPI_CATALOG.map((k) => [k.id, k]),
) as Record<string, KpiCatalogEntry>;

/** Classify a value against thresholds. */
export function classifyThreshold(
  value: number,
  thresholds: KpiThresholds | undefined,
): ThresholdState {
  if (!thresholds) return 'normal';
  if (value >= thresholds.criticalAt) return 'critical';
  if (value >= thresholds.warnAt) return 'warn';
  return 'normal';
}
